/* eslint-disable */

/**
 * CUSTOM VERSION: Works with custom Liquid section and snippets to prevent initial selection of backordered variant
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('variant-picker')) {
  class VariantPicker extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('.js-product');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.optionSelectors = this.querySelectorAll('.option-selector');
      this.data = this.getProductData();
      this.variant = this.getSelectedVariant();

      this.updateLabelTextFromVariant();
      this.initAddToCartButton();
      this.updateAvailability();
      this.updatePrice();
      this.addEventListener('change', this.handleVariantChange.bind(this));
      this.setupAddToCartWarning();
    }

    /**
     * (CUSTOM) Called on element DOM connection
     * Also guards agains second event emission upon possible reconnection
     */
    connectedCallback() {
        if (!this._didInitialEmit) {
            this.emitVariantChange();
            this._didInitialEmit = true;
        }
    }

    /**
     * Called on page load. Forces the Add to Cart button to be enabled and
     * shows the default Add to Cart text, even if no variant is selected yet.
     */

    initAddToCartButton() {
      this.productForm = this.section.querySelector('.js-product-form-main');
      if (!this.productForm) return;

      this.addBtn = this.productForm.querySelector('[name="add"]');
      if (!this.addBtn) return;

      this.addBtn.disabled = false;
      this.addBtn.textContent = this.addBtn.dataset.addToCartText || 'Add to cart';
    }

    /**
     * Called whenever a variant is changed (inside handleVariantChange).
     * If the variant is available => enable + "Add to cart"
     * If the variant is not available => disable + "Unavailable" text
     */
    updateAddToCartButtonOnVariantChange() {
      if (!this.productForm || !this.addBtn) return;

      const selectedOptions = this.getSelectedOptions();
      const allSelected = selectedOptions.every(opt => opt.trim() !== '');

      if (!allSelected) {
        this.addBtn.disabled = false;
        this.addBtn.textContent = this.addBtn.dataset.addToCartText || 'Add to cart';
        return;
      }

      if (!this.variant) {
        this.addBtn.disabled = true;
        this.addBtn.textContent = theme.strings.noVariant || 'Unavailable';
      } else {
        if (this.variant.available) {
          this.addBtn.disabled = false;
          this.addBtn.textContent = this.addBtn.dataset.addToCartText || 'Add to cart';
        } else {
          this.addBtn.disabled = true;
          this.addBtn.textContent = theme.strings.noStock || 'Sold Out';
        }
      }
    }

    /**
     * Handles 'change' events on the variant picker element.
     * @param {object} evt - Event object.
     */
    handleVariantChange(evt) {
      this.variant = null;

      // Get selected variant data (if variant exists).
      this.variant = this.getSelectedVariant();

      if (this.variant) {
        this.updateMedia();
        this.updateUrl(evt);
        this.updateVariantInput();
      } else {
        this.previewMediaFromFirstOption(evt);
      }

      this.updateAddToCartButtonOnVariantChange();
      this.updateAvailability();
      this.updatePrice();
      this.updateWeight();
      this.updateBarcode();
      this.updateBackorderText();
      this.updatePickupAvailability();
      this.updateSku();
      this.validateAllOptionsSelected(); // NEW: validate and remove warnings as user selects options
      this.updateLabelTextFromVariant();
      this.emitVariantChange();
    }

    /**
     * Check if each option (Color, Size, etc.) is selected. If not, show a .options-warning
     * under that option, plus show #variant-warning at the top.
     * @returns {boolean} whether all options are selected
     */
    validateAllOptionsSelected() {
      const selectedOptions = this.getSelectedOptions();
      let allOptionsSelected = true;

      this.optionSelectors.forEach((selector, index) => {
        const labelText =
            selector.querySelector('.label')?.textContent.split(':')[0].trim() ||
            `Option ${index + 1}`;

        let optionWarning = selector.querySelector('.options-warning');

        if (!optionWarning) {
          optionWarning = document.createElement('div');
          optionWarning.classList.add('options-warning');
          selector.appendChild(optionWarning);
        }

        const isSelected = Boolean(selectedOptions[index]);

        optionWarning.style.display = isSelected ? 'none' : 'block';
        optionWarning.textContent = isSelected ? '' : `Please select a ${labelText.toLowerCase()}.`;

        if (!isSelected) allOptionsSelected = false;
      });

      const mainWarningMessage = this.section.querySelector('#variant-warning');

      if (mainWarningMessage) mainWarningMessage.style.display = allOptionsSelected ? 'none' : 'block';

      return allOptionsSelected;
    }

    /**
     * Hide the main #variant-warning and all .options-warning elements.
     */
    hideAllWarnings() {
      const mainWarning = this.section.querySelector('#variant-warning');

      if (mainWarning) mainWarning.style.display = 'none';

      this.optionSelectors.forEach((selector) => {
        const optionWarning = selector.querySelector('.options-warning');
        if (optionWarning) {
          optionWarning.style.display = 'none';
          optionWarning.textContent = '';
        }
      });
    }

    /**
     * Attach a click listener on the Add to Cart button so we can show warnings if no variant
     */
    setupAddToCartWarning() {
      if (!this.productForm) return;

      const addBtn = this.productForm.querySelector('[name="add"]');

      const handleAddToCartClick = (event) => {
        const isValid = this.validateAllOptionsSelected();

        if (!isValid) event.preventDefault();
      };

      if (addBtn) addBtn.addEventListener('click', handleAddToCartClick);
    }

    /**
     * Updates the availability status in option selectors.
     */
    updateAvailability() {
      if (this.dataset.showAvailability === 'false') return;
      if (!this.data || !this.data.product) return;

      const userSelectedOptions = this.getSelectedOptions();
      this.optionSelectors.forEach((selector, selectorIndex) => {
        const optionEls = selector.querySelectorAll('.js-option');
        optionEls.forEach((optionEl) => {
          const candidateValue =
              selector.dataset.selectorType === 'dropdown'
                  ? optionEl.dataset.value
                  : optionEl.value;

          const attemptedSelection = [...userSelectedOptions];
          attemptedSelection[selectorIndex] = candidateValue;

          const matchingVariants = this.data.product.variants.filter((variant) => {
            return variant.options.every((optVal, i) => {
              if (!attemptedSelection[i]) return true;
              return attemptedSelection[i] === optVal;
            });
          });

          let isAvailable = false;
          if (matchingVariants.length > 0) {
            isAvailable = matchingVariants.some((v) => v.available);
          }

          if (!isAvailable) {
            optionEl.classList.add('is-unavailable');
          } else {
            optionEl.classList.remove('is-unavailable');
          }
        });
      });
    }

    /**
     * Updates the backorder text and visibility.
     */
    updateBackorderText() {
      this.backorder = this.backorder || this.section.querySelector('.backorder');
      if (!this.backorder) return;

      let hideBackorder = true;

      if (this.variant && this.variant.available) {
        const { inventory } = this.data.formatted[this.variant.id];

        if (this.variant.inventory_management && inventory === 'none') {
          const backorderProdEl = this.backorder.querySelector('.backorder__product');
          const prodTitleEl = this.section.querySelector('.product-title');
          const variantTitle = this.variant.title.includes('Default')
            ? ''
            : ` - ${this.variant.title}`;

          backorderProdEl.textContent = `${prodTitleEl.textContent}${variantTitle}`;
          hideBackorder = false;
        }
      }

      this.backorder.hidden = hideBackorder;
    }

    /**
     * Updates the product media.
     */
    updateMedia() {
      if (!this.variant.featured_media) return;

      if (this.section.matches('quick-add-drawer')) {
        this.section.updateMedia(this.variant.featured_media.id);
      } else {
        this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
        if (!this.mediaGallery) return;

        const variantMedia = this.mediaGallery.querySelector(
          `[data-media-id="${this.variant.featured_media.id}"]`
        );
        this.mediaGallery.setActiveMedia(variantMedia, true, true);
      }
    }
    
    previewMediaFromFirstOption(evt) {
      const product = this.data?.product;
      if (!product?.variants?.length || !evt?.target) return;
    
      const variantPicker = evt.target.closest('variant-picker');
      if (!variantPicker) return;
    
      const selectedOptions = [];
    
      variantPicker.querySelectorAll('.option-selector').forEach((group, i) => {
        const checkedInput = group.querySelector('input.js-option:checked');
        if (checkedInput) {
          selectedOptions[i] = checkedInput.value;
        }
      });
    
      const fallbackVariant = product.variants.find((variant) => {
        return selectedOptions.every((val, idx) => variant[`option${idx + 1}`] === val);
      });
    
      if (!fallbackVariant?.featured_media) return;
    
      this.mediaGallery = this.mediaGallery || this.section.querySelector('media-gallery');
      if (!this.mediaGallery) return;
    
      const variantMedia = this.mediaGallery.querySelector(
        `[data-media-id="${fallbackVariant.featured_media.id}"]`
      );
    
      if (variantMedia) {
        this.mediaGallery.customSetActiveMedia(variantMedia, true);
      }
    }
    
    
    /**
     * Updates the pick up availability.
     */
    updatePickupAvailability() {
      this.pickUpAvailability =
        this.pickUpAvailability || this.section.querySelector('pickup-availability');
      if (!this.pickUpAvailability) return;

      if (this.variant && this.variant.available) {
        this.pickUpAvailability.getAvailability(this.variant.id);
      } else {
        this.pickUpAvailability.removeAttribute('available');
        this.pickUpAvailability.innerHTML = '';
      }
    }

    /**
     * Interpret `moneyValue` as an integer number of cents (e.g. "5800" => 58.00)
     * Then format as "$58<sup>00</sup>" if superscript = true,
     * or "$58.00" if superscript = false.
     *
     * @param {number|string} moneyValue  e.g. 5800 or "5800"
     * @param {boolean}       superscript e.g. true => "$58<sup>00</sup>"
     * @returns {string} e.g. "$138<sup>00</sup>"
     */
    formatSuperscript(moneyValue, superscript = true) {
      const intVal = parseInt(moneyValue, 10);

      if (isNaN(intVal)) {
        return moneyValue;
      }

      const dollars = Math.floor(intVal / 100);
      const cents = (intVal % 100).toString().padStart(2, '0');

      return !superscript ? `$${dollars}.${cents}` : `$${dollars}<sup>${cents}</sup>`;
    }

    /**
     * Updates the price.
     */
    updatePrice() {
      this.price = this.price || this.section.querySelector('.product-info__price > .price');
      if (!this.price) return;

      const superscriptDecimals = this.data?.settings?.superscript_decimals;

      if (this.variant) {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        const rawPrice = this.data.formatted[this.variant.id].price;
        priceCurrentEl.innerHTML = this.formatSuperscript(rawPrice, superscriptDecimals);

        if (priceWasEl) {
          const rawCompare = this.data.formatted[this.variant.id].compareAtPrice || '';
          priceWasEl.innerHTML = this.formatSuperscript(rawCompare, superscriptDecimals);
        }

        if (this.variant.unit_price_measurement) {
          const valueEl = this.price.querySelector('.unit-price__price');
          const unitEl = this.price.querySelector('.unit-price__unit');
          const value = this.variant.unit_price_measurement.reference_value;
          const unit = this.variant.unit_price_measurement.reference_unit;

          const rawUnitPrice = this.data.formatted[this.variant.id].unitPrice || '';
          valueEl.innerHTML = this.formatSuperscript(rawUnitPrice, superscriptDecimals);
          unitEl.textContent = value === 1 ? unit : `${value} ${unit}`;
          unitPriceEl.hidden = false;
        } else {
          unitPriceEl.hidden = true;
        }

        this.price.classList.toggle(
          'price--on-sale',
          this.variant.compare_at_price > this.variant.price
        );
        this.price.classList.toggle('price--sold-out', !this.variant.available);

      } else {
        const priceCurrentEl = this.price.querySelector('.price__current');
        const priceWasEl = this.price.querySelector('.price__was');
        const unitPriceEl = this.price.querySelector('.unit-price');

        const rawMinPrice = this.data.minPrice;
        const rawMaxPrice = this.data.maxPrice;
        const formattedMin = this.formatSuperscript(rawMinPrice, superscriptDecimals);
        const formattedMax = this.formatSuperscript(rawMaxPrice, superscriptDecimals);

        if (priceCurrentEl) {
          const minInt = parseInt(rawMinPrice, 10);
          const maxInt = parseInt(rawMaxPrice, 10);

          if (minInt === maxInt) {
            priceCurrentEl.innerHTML = formattedMin;
          } else {
            priceCurrentEl.innerHTML = `${formattedMin} – ${formattedMax}`;
          }
        }

        if (priceWasEl) priceWasEl.innerHTML = '';

        if (unitPriceEl) unitPriceEl.hidden = true;

        this.price.classList.remove('price--on-sale', 'price--sold-out');
      }
    }

    /**
     * Updates the weight.
     */
    updateWeight() {
      this.weights = this.weights || this.section.querySelectorAll('.product-info__weight');
      if (this.weights.length === 0) return;

      const weightAvailable = this.variant && this.variant.weight > 0;
      this.weights.forEach((weight) => {
        weight.textContent = weightAvailable ? this.data.formatted[this.variant.id].weight : '';
        weight.hidden = !weightAvailable;
      });
    }

    /**
     * Updates the Barcode.
     */
    updateBarcode() {
      this.barcodes = this.barcodes || this.section.querySelectorAll('.product-info__barcode-value');
      if (this.barcodes.length === 0) return;

      const barcodeAvailable = this.variant && this.variant.barcode;
      this.barcodes.forEach((barcode) => {
        barcode.textContent = barcodeAvailable ? this.variant.barcode : '';
        barcode.parentNode.hidden = !barcodeAvailable;
      });
    }

    /**
     * Updates the SKU.
     */
    updateSku() {
      this.sku = this.sku || this.section.querySelector('.product-sku__value');
      if (!this.sku) return;

      const skuAvailable = this.variant && this.variant.sku;
      this.sku.textContent = skuAvailable ? this.variant.sku : '';
      this.sku.parentNode.hidden = !skuAvailable;
    }

    /**
     * Updates the url with the selected variant id.
     * @param {object} evt - Event object.
     */
    updateUrl(evt) {
      if (!evt || evt.type !== 'change' || this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.variant.id}`);
    }

    /**
     * Updates the value of the hidden [name="id"] form inputs.
     */
    updateVariantInput() {
      this.forms =
        this.forms || this.section.querySelectorAll('.js-product-form-main, .js-instalments-form');

      this.forms.forEach((form) => {
        const input = form.querySelector('input[name="id"]');
        if(!input) return;

        input.value = this.variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    /**
     * Gets the selected option element from each selector.
     * @returns {Array}
     */
    getSelectedOptions() {
      const selectedOptions = [];

      this.optionSelectors.forEach((selector) => {
        if (selector.dataset.selectorType === 'dropdown') {
          selectedOptions.push(selector.querySelector('.custom-select__btn').textContent.trim());
        } else {
          const checkedInput = selector.querySelector('input:checked');
          selectedOptions.push(checkedInput ? checkedInput.value : '');
        }
      });

      return selectedOptions;
    }

    /**
     * Gets the product data.
     * @returns {?object}
     */
    getProductData() {
      const dataEl = this.querySelector('[type="application/json"]');
      return JSON.parse(dataEl.textContent);
    }

    /**
     * Get selected variant data.
     * @returns {?object} Variant object, or null if one is not selected.
     */
    getSelectedVariant() {
      const selectedOptions = this.getSelectedOptions();

      if (selectedOptions.some(option => !option)) return null;

      return this.data.product.variants.find(
        (v) => v.options.every((val, index) => val === selectedOptions[index])
      );
    }

    /**
     * CUSTOM: Called on page load and variant change. Replaces static method reliant on user input.
     * Changes selected variant option text according to selected variant.
     */
    updateLabelTextFromVariant() {
        if (!this.variant) return;

        this.optionSelectors.forEach((selector, index) => {
            if (selector.dataset.selectorType === 'dropdown') return;

            const colorText = selector.querySelector('.js-color-text');
            if (!colorText) return;

            colorText.textContent = this.variant.options[index];
        });
    }

    /**
     * CUSTOM: Called on element connection and variant change.
     * Dispatches custom variant change event.
     */
    emitVariantChange() {
        if (this.variant) {
            this.dispatchEvent(new CustomEvent('on:variant:change', {
                bubbles: true,
                detail: {
                form: this.productForm,
                variant: this.variant,
                product: this.data.product
                }
            }));
        }
    }
  }

  customElements.define('variant-picker', VariantPicker);
}
